import { z } from 'zod';
/**
 * HẸN GIỜ CÔNG KHAI VIDEO (06/10/2026, chủ dự án: «Tôi đã nối được kênh … Tôi muốn vào kênh để mở video công
 * khai lúc 5h sáng»). App đặt GIỜ CÔNG KHAI ngay trên YouTube (`status.publishAt`, đúng như «Lên lịch» trong
 * YouTube Studio): video để riêng tư tới giờ ấy rồi YouTube TỰ mở. Máy chủ của app không phải thức lúc 5 giờ,
 * mất mạng hay khởi động lại cũng không lỡ giờ.
 */
/** Giờ công khai mặc định, giờ Việt Nam. */
export const DEFAULT_PUBLISH_TIME = '05:00';
/** Giờ hẹn phải cách bây giờ ít nhất chừng này — YouTube chỉ nhận giờ ở tương lai. */
export const PUBLISH_LEAD_MINUTES = 15;
/** Hẹn xa nhất — chặn gõ nhầm năm. */
export const PUBLISH_MAX_DAYS_AHEAD = 365;
/** Một lượt thao tác tối đa chừng này video (một lượt hỏi trạng thái của YouTube nhận 50 mã). */
export const PUBLISH_BATCH_LIMIT = 50;
export const YOUTUBE_PRIVACY = ['private', 'unlisted', 'public'];
export const YOUTUBE_PRIVACY_LABELS = {
    private: 'Riêng tư',
    unlisted: 'Không công khai',
    public: 'Công khai',
};
/**
 * Mặc định (06/10/2026, chủ dự án: «cài đặt mặc định tải lên dạng không công khai, phát trực tiếp là dạng công
 * khai»): video tải lên để KHÔNG CÔNG KHAI (hẹn giờ thì riêng tư tới giờ ấy), buổi phát trực tiếp CÔNG KHAI.
 */
export const DEFAULT_UPLOAD_PRIVACY = 'unlisted';
export const DEFAULT_LIVE_PRIVACY = 'public';
const VIETNAM_OFFSET_MS = 7 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const pad = (value) => String(value).padStart(2, '0');
/** Một thời điểm theo giờ Việt Nam: «2026-10-07» và «05:00». */
export function vietnamParts(at) {
    const local = new Date(at.getTime() + VIETNAM_OFFSET_MS);
    return {
        day: `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}`,
        time: `${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`,
    };
}
/** Thời điểm của «ngày `day`, `time` giờ Việt Nam». */
export function vietnamInstant(day, time) {
    const [year, month, date] = day.split('-').map(Number);
    const [hours, minutes] = time.split(':').map(Number);
    return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, date ?? 1, hours ?? 0, minutes ?? 0) -
        VIETNAM_OFFSET_MS);
}
/**
 * Lần tới đồng hồ Việt Nam chỉ `time` mà còn cách bây giờ ít nhất `PUBLISH_LEAD_MINUTES` phút: bấm lúc 14:00
 * thì là 05:00 sáng mai; bấm lúc 02:00 thì là 05:00 sáng nay; bấm lúc 04:50 thì đã quá sát, sang sáng mai.
 */
export function nextPublishTime(now, time = DEFAULT_PUBLISH_TIME) {
    let candidate = vietnamInstant(vietnamParts(now).day, time).getTime();
    while (candidate < now.getTime() + PUBLISH_LEAD_MINUTES * 60_000)
        candidate += DAY_MS;
    return new Date(candidate);
}
/** Lý do một giờ hẹn không dùng được, hoặc `null`. */
export function publishTimeProblem(publishAt, now) {
    if (Number.isNaN(publishAt.getTime()))
        return 'Giờ công khai không hợp lệ';
    if (publishAt.getTime() < now.getTime() + 5 * 60_000) {
        return 'Giờ công khai phải sau bây giờ ít nhất 5 phút';
    }
    if (publishAt.getTime() > now.getTime() + PUBLISH_MAX_DAYS_AHEAD * DAY_MS) {
        return `Chỉ hẹn được trong vòng ${PUBLISH_MAX_DAYS_AHEAD} ngày`;
    }
    return null;
}
/** Số video đọc về mỗi lần (bài tải lên gần nhất) — 50 video tốn 2 đơn vị hạn mức. */
export const YOUTUBE_VIDEO_LIMITS = [100, 200, 500];
export const DEFAULT_YOUTUBE_VIDEO_LIMIT = 100;
export const youtubeVideosQuerySchema = z.object({
    limit: z.coerce.number().int().min(50).max(500).default(DEFAULT_YOUTUBE_VIDEO_LIMIT),
});
export const VIDEO_REJECTION_LABELS = {
    claim: 'bị claim',
    copyright: 'vi phạm bản quyền',
    duplicate: 'trùng video khác',
    inappropriate: 'nội dung không phù hợp',
    legal: 'lý do pháp lý',
    length: 'quá dài',
    termsOfUse: 'vi phạm điều khoản',
    trademark: 'vi phạm nhãn hiệu',
    uploaderAccountClosed: 'tài khoản đã đóng',
    uploaderAccountSuspended: 'tài khoản bị khoá',
};
/**
 * `schedule` hẹn giờ công khai · `publish` công khai ngay · `unlist` không công khai · `unschedule` riêng tư (bỏ
 * hẹn nếu có).
 */
export const YOUTUBE_PUBLISH_ACTIONS = ['schedule', 'publish', 'unlist', 'unschedule'];
export const youtubePublishRequestSchema = z.object({
    action: z.enum(YOUTUBE_PUBLISH_ACTIONS),
    videoIds: z
        .array(z.string().trim().min(1).max(32))
        .min(1, 'Chưa chọn video nào')
        .max(PUBLISH_BATCH_LIMIT, `Mỗi lượt tối đa ${PUBLISH_BATCH_LIMIT} video`),
    /** Giờ công khai (ISO) — chỉ cần với `schedule`. */
    publishAt: z.string().trim().max(40).optional(),
    /** Thêm video vào danh sách phát này sau khi đổi trạng thái; '' / bỏ trống = không thêm. */
    playlistId: z.string().trim().max(100).optional(),
    /** Số video đọc lại trả về sau khi ghi — bằng số giao diện đang xem. */
    limit: z.number().int().min(50).max(500).optional(),
});
export const youtubePublishParamsSchema = z.object({
    youtubeId: z.string().trim().min(1).max(100),
});
/*
 * ------------------------------------------------------------------------------------------------------------
 * TẢI VIDEO LÊN KÊNH + TIÊU ĐỀ · MÔ TẢ · TAG THEO MẪU (06/10/2026, chủ dự án: «Tôi muốn thêm chức năng upload
 * video lên kênh và tự động chỉnh sửa tiêu đề, mô tả, tag theo mẫu»).
 *
 * Tool dựng video của công ty (ToolNhacViet) xuất mỗi bản một thư mục: `<tên>.mp4`, `<tên> - seo.txt` (TIÊU ĐỀ ·
 * MÔ TẢ · TAG), `<tên> - thumb.jpg`. Kéo cả thư mục vào app là đủ: tiêu đề, mô tả, tag lấy từ seo.txt, rồi
 * qua MẪU của kênh (nếu đặt), ảnh bìa gửi theo, giờ công khai hẹn sẵn. File video đi THẲNG từ trình duyệt lên
 * YouTube (phiên tải lên tiếp tục được của Google) — không qua máy chủ của app, vì file 6–18 GB mà đường vào
 * máy chủ chỉ 0,5 Mbit/s qua lớp Bảo vệ DNS (xem ghi chú 28/09/2026).
 * ------------------------------------------------------------------------------------------------------------
 */
/** Hạn mức của YouTube: tiêu đề 100 ký tự, mô tả 5000, cả bộ tag 500 (tính theo luật `tagsLength`). */
export const YOUTUBE_TITLE_MAX = 100;
export const YOUTUBE_DESCRIPTION_MAX = 5000;
export const YOUTUBE_TAGS_MAX = 500;
/** YouTube nhận file tới 256 GB. */
export const YOUTUBE_UPLOAD_MAX_BYTES = 256 * 1024 * 1024 * 1024;
export const youtubePublishTemplateSaveSchema = z.object({
    title: z.string().max(500),
    description: z.string().max(10_000),
    tags: z.string().max(3000),
    playlistId: z.string().trim().max(100),
});
export const TEMPLATE_PLACEHOLDERS = [
    {
        key: '{ten}',
        meaning: 'tiêu đề gốc: trong file seo.txt, không có thì tên file; video đã trên kênh thì tiêu đề hiện có',
    },
    { key: '{tieude}', meaning: 'tiêu đề sau khi áp mẫu (chỉ dùng trong mô tả)' },
    { key: '{mota}', meaning: 'mô tả gốc: trong seo.txt, hoặc mô tả hiện có của video' },
    { key: '{timeline}', meaning: 'các dòng mốc thời gian trong mô tả gốc (0:00:00 01. …)' },
    { key: '{kenh}', meaning: 'tên kênh' },
    { key: '{nam}', meaning: 'năm công khai' },
    { key: '{ngay}', meaning: 'ngày công khai, dd/mm/yyyy' },
];
const TIMELINE_LINE = /^\s*\d{1,2}:\d{2}(?::\d{2})?\s+\S/;
/** Các dòng mốc thời gian («0:05:08 02. …») trong một mô tả — YouTube dùng chúng chia chương. */
export function timelineLines(description) {
    return description
        .split(/\r?\n/)
        .filter((line) => TIMELINE_LINE.test(line))
        .map((line) => line.trim())
        .join('\n');
}
/** Thay `{khoa}` (không phân biệt hoa thường) bằng giá trị; khoá lạ giữ nguyên để người dùng thấy mà sửa. */
export function fillTemplate(text, values) {
    return text.replace(/\{([a-z]+)\}/gi, (whole, key) => {
        const value = values[key.toLowerCase()];
        return value === undefined ? whole : value;
    });
}
/** Tách «a, b , ,c» thành tag sạch, bỏ trùng (không phân biệt hoa thường). */
export function splitTags(text) {
    const seen = new Set();
    const tags = [];
    for (const raw of text.split(/[,\n]/)) {
        const tag = raw.trim().replace(/\s+/g, ' ');
        const key = tag.toLowerCase();
        if (!tag || seen.has(key))
            continue;
        seen.add(key);
        tags.push(tag);
    }
    return tags;
}
/**
 * Độ dài bộ tag theo cách YouTube đếm (tài liệu `videos.insert`): tag có dấu cách tính thêm 2 ký tự
 * (YouTube coi như có ngoặc kép), và dấu phẩy giữa các tag cũng tính.
 */
export function tagsLength(tags) {
    if (!tags.length)
        return 0;
    return (tags.reduce((sum, tag) => sum + tag.length + (/\s/.test(tag) ? 2 : 0), 0) + tags.length - 1);
}
/** Giữ tag theo thứ tự tới khi vừa 500 ký tự; trả số tag phải bỏ ở cuối. */
export function fitTags(tags) {
    const kept = [];
    for (const tag of tags) {
        if (tagsLength([...kept, tag]) > YOUTUBE_TAGS_MAX)
            break;
        kept.push(tag);
    }
    return { tags: kept, dropped: tags.length - kept.length };
}
/**
 * Áp MẪU của kênh lên một bộ tiêu đề · mô tả · tag gốc. Ô mẫu nào để trống thì giữ phần gốc; tag = tag của
 * mẫu rồi tới tag gốc (bỏ trùng), cắt cho vừa 500 ký tự. Tiêu đề thì về một dòng.
 */
export function applyTemplate(template, source, context) {
    const at = context.publishAt ?? context.now;
    const { day } = vietnamParts(at);
    const [year, month, date] = day.split('-');
    const values = {
        ten: source.title.trim(),
        mota: source.description,
        timeline: timelineLines(source.description),
        kenh: context.channelTitle,
        nam: year ?? '',
        ngay: `${date}/${month}/${year}`,
    };
    const title = (template.title.trim() ? fillTemplate(template.title, values) : source.title)
        .replace(/\s+/g, ' ')
        .trim();
    const description = template.description.trim()
        ? fillTemplate(template.description, { ...values, tieude: title })
        : source.description;
    const tags = fitTags(splitTags([template.tags, ...source.tags].join(','))).tags;
    return { title, description: description.replace(/\r\n/g, '\n').trim(), tags };
}
/** Vì sao YouTube sẽ từ chối bộ tiêu đề · mô tả · tag này; rỗng = gửi được. */
export function metadataProblems(meta) {
    const problems = [];
    if (!meta.title.trim())
        problems.push('Thiếu tiêu đề');
    if (meta.title.length > YOUTUBE_TITLE_MAX) {
        problems.push(`Tiêu đề ${meta.title.length}/${YOUTUBE_TITLE_MAX} ký tự`);
    }
    if (/[<>]/.test(meta.title))
        problems.push('Tiêu đề không được chứa dấu < >');
    if (meta.description.length > YOUTUBE_DESCRIPTION_MAX) {
        problems.push(`Mô tả ${meta.description.length}/${YOUTUBE_DESCRIPTION_MAX} ký tự`);
    }
    if (/[<>]/.test(meta.description))
        problems.push('Mô tả không được chứa dấu < >');
    const length = tagsLength(meta.tags);
    if (length > YOUTUBE_TAGS_MAX)
        problems.push(`Tag ${length}/${YOUTUBE_TAGS_MAX} ký tự`);
    return problems;
}
const metadataSchema = z.object({
    title: z.string().trim().min(1, 'Thiếu tiêu đề').max(YOUTUBE_TITLE_MAX),
    description: z.string().max(YOUTUBE_DESCRIPTION_MAX),
    tags: z.array(z.string().trim().min(1).max(100)).max(100),
});
/** Mở một phiên tải video lên kênh: máy chủ xin YouTube địa chỉ phiên, trình duyệt tự đẩy file vào đó. */
export const youtubeUploadStartSchema = metadataSchema.extend({
    fileName: z.string().trim().min(1).max(300),
    fileSize: z.number().int().positive().max(YOUTUBE_UPLOAD_MAX_BYTES),
    mimeType: z.string().trim().min(1).max(100),
    /** Giờ công khai (ISO); `null` = không hẹn, hiển thị theo `privacy`. */
    publishAt: z.string().trim().max(40).nullable(),
    /** Hiển thị khi KHÔNG hẹn giờ; có `publishAt` thì YouTube bắt phải riêng tư (máy chủ tự ép). */
    privacy: z.enum(YOUTUBE_PRIVACY).default('private'),
});
/** Báo máy chủ một video đã lên xong: ghi nhật ký, thêm vào danh sách phát, trả dòng của nó cho danh sách. */
export const youtubeUploadFinishSchema = z.object({
    videoId: z.string().trim().min(1).max(32),
    playlistId: z.string().trim().max(100).optional(),
});
/** Ảnh bìa gửi qua máy chủ (nhỏ, tới 2 MB theo YouTube). */
export const YOUTUBE_THUMBNAIL_MAX_BYTES = 2 * 1024 * 1024;
export const youtubeVideoParamsSchema = youtubePublishParamsSchema.extend({
    videoId: z.string().trim().min(1).max(32),
});
/** Sửa tiêu đề · mô tả · tag của các video đã trên kênh («Áp mẫu»). */
export const youtubeMetadataRequestSchema = z.object({
    items: z
        .array(metadataSchema.extend({ id: z.string().trim().min(1).max(32) }))
        .min(1, 'Chưa chọn video nào')
        .max(PUBLISH_BATCH_LIMIT, `Mỗi lượt tối đa ${PUBLISH_BATCH_LIMIT} video`),
    limit: z.number().int().min(50).max(500).optional(),
});
/**
 * SỬA MỘT VIDEO TRÊN KÊNH TRỌN GÓI (07/10/2026, chủ dự án: «chỉnh sửa hiển thị (công khai, không công khai, riêng
 * tư, hẹn lịch) · tích chọn, bỏ các playlist có sẵn trong kênh»): tiêu đề · mô tả · tag + hiển thị + danh sách
 * phát trong một lượt. Máy chủ ghi `videos.update` MỘT lần (part=snippet,status khi đổi hiển thị; 50 đơn vị), rồi
 * thêm / bỏ khỏi từng danh sách phát (`playlistItems.insert` 50; `playlistItems.list` 1 + `delete` 50).
 */
export const VIDEO_VISIBILITY_CHOICES = [
    'keep',
    'public',
    'unlisted',
    'private',
    'schedule',
];
export const VIDEO_VISIBILITY_LABELS = {
    keep: 'Giữ nguyên',
    public: 'Công khai',
    unlisted: 'Không công khai',
    private: 'Riêng tư',
    schedule: 'Hẹn lịch',
};
export const youtubeVideoEditSchema = z.object({
    meta: metadataSchema,
    visibility: z.enum(VIDEO_VISIBILITY_CHOICES).default('keep'),
    /** Giờ công khai (ISO) — chỉ cần với `schedule`. */
    publishAt: z.string().trim().max(40).optional(),
    playlists: z
        .object({
        add: z.array(z.string().trim().min(1).max(100)).max(50).default([]),
        remove: z.array(z.string().trim().min(1).max(100)).max(50).default([]),
    })
        .default({ add: [], remove: [] }),
    limit: z.number().int().min(50).max(500).optional(),
});
/** Lệnh đổi trạng thái tương ứng với lựa chọn hiển thị; `keep` → null (không đụng phần `status`). */
export function visibilityAction(choice) {
    switch (choice) {
        case 'public':
            return 'publish';
        case 'unlisted':
            return 'unlist';
        case 'private':
            return 'unschedule';
        case 'schedule':
            return 'schedule';
        default:
            return null;
    }
}
/**
 * TẠO PODCAST (08/10/2026, chủ dự án: «Chức năng tạo Podcast và add video vào podcast giống như danh sách phát»).
 * Podcast của YouTube là một danh sách phát có `podcastStatus = enabled`; YouTube bắt danh sách phải có ẢNH VUÔNG
 * (`playlistImages`) trước. Trình duyệt tự cắt ảnh thành vuông; thông tin đi trên query, ảnh là thân yêu cầu.
 * Tốn 150 đơn vị: tạo danh sách 50 + ảnh 50 + đặt podcast 50. Thêm video vào podcast như danh sách phát.
 */
export const PODCAST_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
export const youtubePodcastCreateSchema = z.object({
    title: z.string().trim().min(1).max(150),
    description: z.string().max(5000).default(''),
    privacy: z.enum(['private', 'unlisted', 'public']).default('public'),
});
/**
 * VIDEO NẰM Ở DANH SÁCH PHÁT NÀO (06/10/2026, chủ dự án: «cập nhật thêm giờ mở, danh sách phát, view, like,
 * comment»): mỗi danh sách của kênh kèm mã video trong đó — đọc `playlistItems.list` từng danh sách, 1 đơn vị
 * hạn mức mỗi trang 50, tối đa `PLAYLIST_MEMBERSHIP_PAGES` trang (danh sách dài hơn chỉ thấy 200 video đầu).
 * Phần Video ghép thành cột «Danh sách phát».
 */
export const PLAYLIST_MEMBERSHIP_PAGES = 4;
/**
 * TẠO DANH SÁCH PHÁT ngay trong app (06/10/2026, chủ dự án: «Tạo danh sách phát bằng API»): `playlists.insert`,
 * 50 đơn vị. YouTube nhận tiêu đề tới 150 ký tự, mô tả tới 5000.
 */
export const PLAYLIST_TITLE_MAX = 150;
export const PLAYLIST_DESCRIPTION_MAX = 5000;
export const youtubePlaylistCreateSchema = z.object({
    title: z.string().trim().min(1).max(PLAYLIST_TITLE_MAX),
    description: z.string().max(PLAYLIST_DESCRIPTION_MAX).default(''),
    privacy: z.enum(YOUTUBE_PRIVACY).default('public'),
});
/*
 * ------------------------------------------------------------------------------------------------------------
 * TỐI ƯU KÊNH (06/10/2026, chủ dự án: «thay ảnh kênh, tên kênh, mô tả kênh, set quốc gia, từ khóa kênh»).
 * YouTube API chỉ cho sửa: mô tả, từ khoá, quốc gia, ngôn ngữ, video giới thiệu, ẢNH BÌA (banner). Tên kênh và
 * ảnh đại diện API không sửa được (tài liệu `channels.update` không liệt kê `title`; ảnh đại diện không có
 * phương thức nào) — màn hình hiện hai thứ ấy kèm đường sang YouTube Studio.
 * ------------------------------------------------------------------------------------------------------------
 */
export const CHANNEL_DESCRIPTION_MAX = 1000;
/** Từ khoá kênh: YouTube đếm cả chuỗi «a "b c" d» ≤ 500 ký tự. */
export const CHANNEL_KEYWORDS_MAX = 500;
/** Ảnh bìa kênh: JPG/PNG, YouTube đòi ít nhất 2048×1152, khuyên 2560×1440; app nhận tới 6 MB. */
export const CHANNEL_BANNER_MAX_BYTES = 6 * 1024 * 1024;
export const CHANNEL_BANNER_MIN_WIDTH = 2048;
export const CHANNEL_BANNER_MIN_HEIGHT = 1152;
export const youtubeChannelBrandingSaveSchema = z.object({
    description: z.string().max(CHANNEL_DESCRIPTION_MAX),
    keywords: z.array(z.string().trim().min(1).max(100)).max(100),
    country: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^([A-Z]{2})?$/, 'Mã quốc gia là 2 chữ cái'),
    defaultLanguage: z.string().trim().max(10),
    unsubscribedTrailer: z.string().trim().max(32),
});
/** Tách chuỗi từ khoá kiểu YouTube: «nhạc "nhạc vàng" bolero» → ['nhạc', 'nhạc vàng', 'bolero']. */
export function parseChannelKeywords(text) {
    const keywords = [];
    const seen = new Set();
    for (const match of text.matchAll(/"([^"]*)"|(\S+)/g)) {
        const keyword = (match[1] ?? match[2] ?? '').trim().replace(/\s+/g, ' ');
        const key = keyword.toLowerCase();
        if (!keyword || seen.has(key))
            continue;
        seen.add(key);
        keywords.push(keyword);
    }
    return keywords;
}
/** Ghép lại theo đúng kiểu YouTube: từ khoá có dấu cách thì bọc ngoặc kép. */
export function formatChannelKeywords(keywords) {
    return keywords
        .map((keyword) => keyword.trim())
        .filter(Boolean)
        .map((keyword) => (/\s/.test(keyword) ? `"${keyword.replace(/"/g, '')}"` : keyword))
        .join(' ');
}
/** Vì sao YouTube sẽ từ chối phần mô tả kênh này; rỗng = gửi được. */
export function channelBrandingProblems(input) {
    const problems = [];
    if (input.description.length > CHANNEL_DESCRIPTION_MAX) {
        problems.push(`Mô tả kênh ${input.description.length}/${CHANNEL_DESCRIPTION_MAX} ký tự`);
    }
    if (/[<>]/.test(input.description))
        problems.push('Mô tả kênh không được chứa dấu < >');
    const keywords = formatChannelKeywords(input.keywords);
    if (keywords.length > CHANNEL_KEYWORDS_MAX) {
        problems.push(`Từ khoá kênh ${keywords.length}/${CHANNEL_KEYWORDS_MAX} ký tự`);
    }
    return problems;
}
/** Quốc gia hay gặp với kênh nhạc Việt; kênh đang đặt nước khác thì giao diện thêm mã ấy vào. */
export const CHANNEL_COUNTRY_OPTIONS = [
    { value: 'VN', label: 'Việt Nam' },
    { value: 'US', label: 'Hoa Kỳ' },
    { value: 'AU', label: 'Úc' },
    { value: 'CA', label: 'Canada' },
    { value: 'GB', label: 'Anh' },
    { value: 'FR', label: 'Pháp' },
    { value: 'DE', label: 'Đức' },
    { value: 'CZ', label: 'Séc' },
    { value: 'PL', label: 'Ba Lan' },
    { value: 'RU', label: 'Nga' },
    { value: 'JP', label: 'Nhật Bản' },
    { value: 'KR', label: 'Hàn Quốc' },
    { value: 'TW', label: 'Đài Loan' },
    { value: 'CN', label: 'Trung Quốc' },
    { value: 'HK', label: 'Hồng Kông' },
    { value: 'TH', label: 'Thái Lan' },
    { value: 'LA', label: 'Lào' },
    { value: 'KH', label: 'Campuchia' },
    { value: 'SG', label: 'Singapore' },
    { value: 'MY', label: 'Malaysia' },
    { value: 'PH', label: 'Philippines' },
    { value: 'ID', label: 'Indonesia' },
    { value: 'IN', label: 'Ấn Độ' },
    { value: 'BR', label: 'Brazil' },
    { value: 'MX', label: 'Mexico' },
];
export const CHANNEL_LANGUAGE_OPTIONS = [
    { value: 'vi', label: 'Tiếng Việt' },
    { value: 'en', label: 'Tiếng Anh' },
    { value: 'en-US', label: 'Tiếng Anh (Mỹ)' },
    { value: 'fr', label: 'Tiếng Pháp' },
    { value: 'de', label: 'Tiếng Đức' },
    { value: 'ja', label: 'Tiếng Nhật' },
    { value: 'ko', label: 'Tiếng Hàn' },
    { value: 'zh-Hans', label: 'Tiếng Trung (giản thể)' },
    { value: 'zh-Hant', label: 'Tiếng Trung (phồn thể)' },
    { value: 'th', label: 'Tiếng Thái' },
    { value: 'id', label: 'Tiếng Indonesia' },
    { value: 'es', label: 'Tiếng Tây Ban Nha' },
];
/**
 * XOAY VÒNG NHIỀU CLIENT (08/10/2026, chủ dự án: «nhập nhiều client ID và client secret để xoay vòng»): mỗi client
 * là một dự án Google Cloud với hạn mức riêng — 10.000 đơn vị và 100 lượt tải lên mỗi ngày, mở lại lúc nửa đêm giờ
 * Thái Bình Dương. Số đơn vị là app tự đếm (gần đúng); cờ «hết» là Google đã báo.
 */
export const PUBLISH_DAILY_UNITS = 10_000;
export const PUBLISH_DAILY_UPLOADS = 100;
export const youtubePublishClientSaveSchema = z.object({
    /** Có = sửa client ấy (tên / secret); không = thêm mới, hoặc cập nhật client trùng Client ID. */
    key: z.string().trim().max(40).optional(),
    name: z.string().trim().max(60).default(''),
    clientId: z.string().trim().min(1).max(300),
    clientSecret: z.string().trim().min(1).max(300),
});
export const youtubePublishClientParamsSchema = z.object({
    key: z.string().trim().min(1).max(40),
});
export const youtubePublishChannelHomeSchema = z.object({
    /** '' = bỏ giao. */
    clientKey: z.string().trim().max(40).default(''),
});
export const youtubePublishConnectStartSchema = z.object({
    /** Nối bằng client nào; bỏ trống = client đầu tiên. */
    clientKey: z.string().trim().max(40).optional(),
});
/*
 * ------------------------------------------------------------------------------------------------------------
 * PHÁT TRỰC TIẾP (06/10/2026, chủ dự án: «Tôi muốn lên lịch phát trực tiếp: tối ưu mô tả, tiêu đề, tag, upload
 * thum. Bạn thiết kế tính năng đó vào phần đăng video»). Buổi phát = `liveBroadcasts` của YouTube Live API; tag
 * ghi qua video cùng mã (`videos.update`); ảnh bìa qua đường `thumbnails.set` sẵn có.
 * ------------------------------------------------------------------------------------------------------------
 */
export const LIVE_LATENCIES = ['normal', 'low', 'ultraLow'];
export const LIVE_LATENCY_LABELS = {
    normal: 'Bình thường — ổn định nhất',
    low: 'Thấp',
    ultraLow: 'Cực thấp',
};
/** Trạng thái vòng đời YouTube trả về → chữ trên màn. */
export const LIVE_LIFECYCLE_LABELS = {
    created: 'Đã lên lịch',
    ready: 'Sẵn sàng',
    testStarting: 'Đang thử',
    testing: 'Đang thử',
    liveStarting: 'Đang bắt đầu',
    live: 'Đang phát',
    complete: 'Đã kết thúc',
    revoked: 'Đã thu hồi',
    reclaimed: 'Đã thu hồi',
    abandoned: 'Bỏ dở',
};
/** Giờ bắt đầu mặc định của buổi phát (08/10/2026, chủ dự án: «set giờ 04:00 của ngày hôm sau»). */
export const DEFAULT_LIVE_TIME = '04:00';
export const LIVE_LEAD_MINUTES = 1;
/**
 * QUẢNG CÁO GIỮA BUỔI PHÁT (06/10/2026, chủ dự án: «sét tiền quảng cáo video live»): YouTube Data API chỉ cho
 * đặt lịch chèn quảng cáo của BUỔI PHÁT TRỰC TIẾP (`monetizationDetails.cuepointSchedule`, kênh phải đang bật
 * kiếm tiền). Quảng cáo của video thường không có API — đặt trong Studio (`studioMonetizationUrl`).
 */
export const LIVE_AD_STRATEGIES = ['CONCURRENT', 'NON_CONCURRENT'];
export const LIVE_AD_STRATEGY_LABELS = {
    CONCURRENT: 'Cùng lúc cho mọi người xem',
    NON_CONCURRENT: 'Mỗi người xem một lượt riêng',
};
/** Khoảng cách giữa hai lượt quảng cáo, giây. */
export const LIVE_AD_INTERVALS = [300, 600, 900, 1200, 1800, 3600];
export const youtubeLiveSaveSchema = z.object({
    title: z.string().trim().min(1).max(YOUTUBE_TITLE_MAX),
    description: z.string().max(YOUTUBE_DESCRIPTION_MAX).default(''),
    tags: z.array(z.string().trim().min(1).max(100)).max(200).default([]),
    /** Giờ bắt đầu, ISO 8601. */
    scheduledStartAt: z
        .string()
        .refine((value) => Number.isFinite(Date.parse(value)), 'Giờ bắt đầu không hợp lệ'),
    privacy: z.enum(YOUTUBE_PRIVACY).default(DEFAULT_LIVE_PRIVACY),
    /** Khoá luồng của kênh để gắn vào buổi phát; '' = chọn trong Studio lúc lên sóng. */
    streamId: z.string().trim().max(100).default(''),
    autoStart: z.boolean().default(true),
    autoStop: z.boolean().default(true),
    dvr: z.boolean().default(true),
    recordFromStart: z.boolean().default(true),
    latency: z.enum(LIVE_LATENCIES).default('normal'),
    madeForKids: z.boolean().default(false),
    /** Chèn quảng cáo giữa buổi phát (kênh phải bật kiếm tiền) — chỉ gửi lên YouTube khi `enabled`. */
    ads: z
        .object({
        enabled: z.boolean().default(false),
        intervalSeconds: z.number().int().min(60).max(3600).default(600),
        strategy: z.enum(LIVE_AD_STRATEGIES).default('CONCURRENT'),
    })
        .default({ enabled: false, intervalSeconds: 600, strategy: 'CONCURRENT' }),
});
export const youtubeLiveParamsSchema = youtubePublishParamsSchema.extend({
    broadcastId: z.string().trim().min(1).max(32),
});
export const youtubeLiveStreamParamsSchema = youtubePublishParamsSchema.extend({
    streamId: z.string().trim().min(1).max(100),
});
/** XOÁ VIDEO (06/10/2026, chủ dự án: «chức năng riêng tư và xóa video»): `videos.delete`, không hoàn tác được. */
export const youtubeVideosDeleteSchema = z.object({
    videoIds: z
        .array(z.string().trim().min(1).max(32))
        .min(1, 'Chưa chọn video nào')
        .max(PUBLISH_BATCH_LIMIT, `Mỗi lượt tối đa ${PUBLISH_BATCH_LIMIT} video`),
    limit: z.number().int().min(50).max(500).optional(),
});
/** Quảng cáo của video thường chỉ đặt được trong Studio — mở đúng mục. */
/** Link YouTube rút gọn của một video (07/10/2026, chủ dự án: «Kèm link Youtube thu gọn phía dưới tiêu đề»). */
export function youtubeShortUrl(videoId) {
    return `https://youtu.be/${encodeURIComponent(videoId)}`;
}
export function studioMonetizationUrl(videoId) {
    return `https://studio.youtube.com/video/${videoId}/monetization`;
}
export function liveStartProblem(iso, now) {
    const at = Date.parse(iso);
    if (!Number.isFinite(at))
        return 'Giờ bắt đầu không hợp lệ';
    if (at < now.getTime() + LIVE_LEAD_MINUTES * 60_000) {
        return 'Giờ bắt đầu phải sau lúc này ít nhất 1 phút';
    }
    return '';
}
/** Giờ bắt đầu mặc định: 04:00 giờ Việt Nam của NGÀY HÔM SAU — kể cả khi bây giờ chưa tới 04:00. */
export function nextLiveStart(now, time = DEFAULT_LIVE_TIME) {
    const today = vietnamInstant(vietnamParts(now).day, time);
    return new Date(today.getTime() + DAY_MS);
}
export function liveStudioUrl(broadcastId) {
    return `https://studio.youtube.com/video/${broadcastId}/livestreaming`;
}
export function liveStateLabel(lifeCycle) {
    return LIVE_LIFECYCLE_LABELS[lifeCycle] ?? lifeCycle;
}
/*
 * ------------------------------------------------------------------------------------------------------------
 * NHÓM KÊNH (06/10/2026, chủ dự án: «Tôi muốn chia nhóm kênh, bạn thiết kế từng file nhóm kênh tôi dễ quản lý»):
 * kênh đã nối xếp vào nhóm như thư mục; ô chọn kênh hiện theo nhóm, phần Kết nối tạo / đổi tên / xoá nhóm.
 * ------------------------------------------------------------------------------------------------------------
 */
export const PUBLISH_GROUP_NAME_MAX = 60;
export const youtubePublishGroupSaveSchema = z.object({
    name: z.string().trim().min(1, 'Nhập tên nhóm').max(PUBLISH_GROUP_NAME_MAX),
});
export const youtubePublishGroupParamsSchema = z.object({
    groupId: z.string().trim().min(1).max(40),
});
/** '' = bỏ khỏi nhóm. */
export const youtubePublishChannelGroupSchema = z.object({
    groupId: z.string().trim().max(40).default(''),
});
/**
 * THÔNG TIN TỰ NHẬP CỦA KÊNH ĐÃ NỐI (07/10/2026, chủ dự án: «Thêm cột Network, trạng thái kênh (bật kiếm tiền,
 * chưa bật, kênh duyệt xịt) · Thêm cột mail quản lý kênh tự nhập»). YouTube Data API không trả tình trạng kiếm
 * tiền của kênh, nên ba ô này do người quản lý tự ghi, lưu kèm dòng kết nối (migration 0032).
 */
export const CHANNEL_MONETIZATION = ['unknown', 'monetized', 'not_enabled', 'rejected'];
export const CHANNEL_MONETIZATION_LABELS = {
    unknown: 'Chưa rõ',
    monetized: 'Bật kiếm tiền',
    not_enabled: 'Chưa bật',
    rejected: 'Duyệt xịt',
};
export const CHANNEL_NETWORK_MAX = 100;
export const CHANNEL_MANAGER_EMAIL_MAX = 200;
export const youtubePublishChannelInfoSchema = z.object({
    network: z.string().trim().max(CHANNEL_NETWORK_MAX).default(''),
    monetization: z.enum(CHANNEL_MONETIZATION).default('unknown'),
    managerEmail: z
        .string()
        .trim()
        .max(CHANNEL_MANAGER_EMAIL_MAX)
        .refine((value) => value === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value), 'Mail quản lý không hợp lệ')
        .default(''),
});
/** Giá trị lưu trong CSDL → một trong các mức; lạ thì «chưa rõ». */
export function channelMonetizationOf(value) {
    return CHANNEL_MONETIZATION.includes(value)
        ? value
        : 'unknown';
}
/*
 * ------------------------------------------------------------------------------------------------------------
 * LIÊN KẾT KÊNH (09/10/2026, chủ dự án: «tương tác kênh với các kênh cùng key» → làm cả 4 mục, tab riêng).
 * Giới thiệu chéo giữa các kênh cùng nhóm bằng công cụ chính chủ YouTube cho phép — không tự thích / bình luận /
 * đăng ký chéo (tương tác giả).
 * ------------------------------------------------------------------------------------------------------------
 */
export const CROSSPROMO_ACTIONS = ['featured', 'playlist', 'comment'];
export const CROSSPROMO_DEFAULT_PLAYLIST_TITLE = 'Tuyển chọn';
export const CROSSPROMO_DEFAULT_COMMENT = 'Nghe thêm các kênh cùng chủ đề:';
export const CROSSPROMO_PER_CHANNEL_MAX = 50;
export const CROSSPROMO_GROUP_MAX = 50;
export const youtubeCrossPromoRunSchema = z.object({
    /** Các kênh trong nhóm theo đúng thứ tự trên bảng — máy chủ xoay vòng kênh anh em theo thứ tự này. */
    group: z.array(z.string().trim().min(1).max(64)).min(2).max(CROSSPROMO_GROUP_MAX),
    playlistTitle: z.string().trim().min(1).max(150).default(CROSSPROMO_DEFAULT_PLAYLIST_TITLE),
    perChannel: z.coerce.number().int().min(1).max(CROSSPROMO_PER_CHANNEL_MAX).default(2),
    commentIntro: z.string().trim().max(500).default(CROSSPROMO_DEFAULT_COMMENT),
});
export const youtubeCrossPromoParamsSchema = z.object({
    youtubeId: z.string().trim().min(1).max(64),
    action: z.enum(CROSSPROMO_ACTIONS),
});
/**
 * Kênh anh em để giới thiệu, XOAY theo vị trí của kênh trong nhóm: kênh thứ i bắt đầu từ kênh i+1 — mỗi kênh giới
 * thiệu một bộ khác nhau, kênh nào cũng được nhắc tới. Không bao giờ tự giới thiệu mình.
 */
export function rotatePartners(self, group, count) {
    const others = group.filter((id) => id !== self);
    if (!others.length || count <= 0)
        return [];
    const index = Math.max(0, group.indexOf(self));
    const start = index % others.length;
    const rotated = [...others.slice(start), ...others.slice(0, start)];
    return rotated.slice(0, count);
}
/** Khối link trong Mẫu SEO mô tả: nằm giữa hai dòng đánh dấu để lần sau app thay đúng khối ấy. */
export const CROSSPROMO_BLOCK_START = '--- Kênh cùng chủ đề ---';
export const CROSSPROMO_BLOCK_END = '--- ---';
/*
 * ------------------------------------------------------------------------------------------------------------
 * LINK THƯ MỤC GOOGLE DRIVE (09/10/2026, chủ dự án: «Phát trực tiếp: thêm chức bỏ link drive … setup được Thum,
 * tiêu đề, mô tả, tag»): dán link thư mục tool xuất ra (đã chia sẻ «Bất kỳ ai có đường liên kết»), máy chủ lấy
 * seo.txt + ảnh bìa về — file video trong thư mục bỏ qua.
 * ------------------------------------------------------------------------------------------------------------
 */
export const youtubeDriveFolderSchema = z.object({
    url: z.string().trim().min(1, 'Dán link thư mục Google Drive').max(500),
});
/*
 * ------------------------------------------------------------------------------------------------------------
 * XẾP DANH SÁCH PHÁT THEO LƯỢT XEM 60 PHÚT (09/10/2026, chủ dự án: «sắp xếp lại danh sách phát video có view 60p
 * nhiều xuống bé, danh sách phát tầm 10 video»). Lượt xem 60 phút lấy từ mẫu app tự đo 10 phút một lần.
 * ------------------------------------------------------------------------------------------------------------
 */
export const youtubePlaylistParamsSchema = z.object({
    youtubeId: z.string().trim().min(1).max(64),
    playlistId: z.string().trim().min(1).max(100),
});
/** Xếp theo thứ tự tự chọn (10/10/2026, «tôi chỉnh kéo video»): toàn bộ mã mục theo thứ tự mới. */
export const youtubePlaylistReorderSchema = z.object({
    itemIds: z.array(z.string().trim().min(1).max(100)).min(1).max(200),
});
/**
 * Thêm video vào danh sách phát ngay trong hộp Xếp (10/10/2026, chủ dự án: «Danh sách tôi mới tạo chưa có video tôi
 * muốn bạn hiện video tôi tích add vào và sắp xếp danh sách phát»): video đã có trong danh sách thì bỏ qua.
 */
export const youtubePlaylistAddSchema = z.object({
    videoIds: z
        .array(z.string().trim().min(1).max(32))
        .min(1, 'Chưa chọn video nào')
        .max(PUBLISH_BATCH_LIMIT, `Mỗi lượt tối đa ${PUBLISH_BATCH_LIMIT} video`),
});
/** Bỏ một video khỏi danh sách phát (10/10/2026, «thêm chức năng xóa video khỏi playlist»). */
export const youtubePlaylistItemParamsSchema = youtubePlaylistParamsSchema.extend({
    itemId: z.string().trim().min(1).max(100),
});
/**
 * Các lượt dời để biến `current` thành `target`, làm lần lượt từ đầu: ở vị trí i mà mục chưa đúng thì dời mục đích
 * về i (`playlistItems.update` với `position = i`) — các mục sau tự lùi một chỗ, như YouTube làm. Mục đã đúng chỗ
 * không tốn lượt nào. Dùng chung: máy chủ dời thật, giao diện đếm trước số lượt (50 đơn vị mỗi lượt).
 */
export function playlistMoveSteps(current, target) {
    const order = [...current];
    const steps = [];
    target.forEach((itemId, position) => {
        if (order[position] === itemId)
            return;
        const from = order.indexOf(itemId);
        if (from < 0)
            return;
        order.splice(from, 1);
        order.splice(position, 0, itemId);
        steps.push({ itemId, position });
    });
    return steps;
}
//# sourceMappingURL=youtube-publish.js.map